import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Activity,
  AlertTriangle,
  Info,
  ShieldOff,
  Wrench,
  Radio,
} from 'lucide-react';
import type { FleetSummary } from '../types';

interface InactiveSensorsSummaryProps {
  summary: FleetSummary | undefined;
  isLoading: boolean;
}

interface SummaryCardProps {
  title: string;
  tooltip: string;
  value: number | undefined;
  icon: React.ReactNode;
  isLoading: boolean;
  valueClassName?: string;
}

function SummaryCard({
  title,
  tooltip,
  value,
  icon,
  isLoading,
  valueClassName,
}: SummaryCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-muted-foreground/50 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[220px]">
                <p className="text-xs">{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          {icon}
        </div>
        {isLoading ? (
          <Skeleton className="h-8 w-16 mt-2" />
        ) : (
          <p className={`text-2xl font-bold mt-2 ${valueClassName || ''}`}>
            {value ?? 0}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Fleet-wide summary stat cards for the Inactive Sensors page
 */
export function InactiveSensorsSummary({
  summary,
  isLoading,
}: InactiveSensorsSummaryProps) {
  return (
    <TooltipProvider delayDuration={300}>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <SummaryCard
          title="Total Fleet"
          tooltip="All non-deleted, non-archived sensors across all customer accounts."
          value={summary?.total}
          icon={<Radio className="h-4 w-4 text-muted-foreground" />}
          isLoading={isLoading}
        />
        <SummaryCard
          title="Active"
          tooltip="Sensors reporting data, including those in alert or inhibited state."
          value={summary?.active}
          icon={<Activity className="h-4 w-4 text-green-500" />}
          isLoading={isLoading}
          valueClassName="text-green-600"
        />
        <SummaryCard
          title="Inactive"
          tooltip="Sensors marked as inactive (status 5) - no longer reporting data."
          value={summary?.inactive}
          icon={<AlertTriangle className="h-4 w-4 text-red-500" />}
          isLoading={isLoading}
          valueClassName="text-red-600"
        />
        <SummaryCard
          title="Maintenance"
          tooltip="Sensors temporarily taken offline for maintenance or servicing."
          value={summary?.maintenance}
          icon={<Wrench className="h-4 w-4 text-yellow-500" />}
          isLoading={isLoading}
          valueClassName="text-yellow-600"
        />
        <SummaryCard
          title="Inhibited"
          tooltip="Sensors with alerts suppressed. Included in the Active count."
          value={summary?.inhibited}
          icon={<ShieldOff className="h-4 w-4 text-orange-500" />}
          isLoading={isLoading}
          valueClassName="text-orange-600"
        />
      </div>
    </TooltipProvider>
  );
}
